<script setup lang="ts">
/**
 * Пустое состояние по макету: иконка на плитке-тинте, заголовок, подсказка и
 * одна CTA. Одна структура на все такие экраны трекера — «нет проектов»,
 * «доска пуста», «в базе знаний пусто», «нет доступа»: в макете они
 * отличаются только иконкой, тоном плитки и текстом.
 *
 * Своё, а не AppEmptyState из дизайн-системы: там иконка без подложки и
 * заголовок 18px, а по макету плитка 72 с радиусом 24 и заголовок 24. Менять
 * общий компонент — регресс во всех пустых состояниях CRM.
 */
import type { Component } from 'vue'

withDefaults(
  defineProps<{
    icon: Component
    title: string
    description?: string
    /** Фон плитки: в макете у каждого пустого состояния свой тон */
    tint?: string
  }>(),
  { description: '', tint: 'var(--color-primary-tint)' },
)
</script>

<template>
  <div class="empty-state">
    <div class="empty-state__tile" :style="{ background: tint }">
      <component :is="icon" :size="30" :stroke-width="2" />
    </div>

    <h1 class="empty-state__title">{{ title }}</h1>
    <p v-if="description" class="empty-state__text">{{ description }}</p>

    <div v-if="$slots.default" class="empty-state__cta">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.empty-state {
  display: flex;
  /* Занимает всю свободную высоту и центрируется в ней: в макете это блок
     внутри карточки, но на своей странице прижатый к верху текст выглядит
     обрезанным. По горизонтали и внутри блока метрики макета сохранены. */
  flex: 1;
  min-height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
}

.empty-state__tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  /* Ровно треть стороны — токена такого радиуса в дизайн-системе нет,
     ближайший --radius-lg (16) заметно площе макета. */
  border-radius: 24px;
  color: var(--color-primary);
}

.empty-state__title {
  margin: 14px 0 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  line-height: 36px;
}

.empty-state__text {
  max-width: 380px;
  margin: 14px 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: 21px;
}

.empty-state__cta {
  margin-top: 14px;
}
</style>
